import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import Seo from "@/components/Seo";

const money = (c) => `$${(c / 100).toFixed(2)}`;

export default function QuickPlays() {
  const [plays, setPlays] = useState([]);
  useEffect(() => { trackEvent("quick_play_viewed"); (async () => { try { const { data } = await api.get("/quick-plays"); setPlays(data || []); } catch { /* */ } })(); }, []);

  return (
    <div data-testid="quick-plays-landing">
      <Seo title="Quick Plays" description="Answer a few questions. Get a personalized plan and a PDF in minutes — for parents, coaches, and players." path="/quick-plays" />

      {/* hero */}
      <section className="bg-[color:var(--vt-dark)] vt-grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <span className="vt-eyebrow">Voluntold Quick Plays</span>
          <h1 className="mt-3 font-head text-[color:var(--vt-white)] text-6xl lg:text-[120px] leading-[0.86]">HELP FOR RIGHT NOW.</h1>
          <p className="mt-8 text-[color:var(--vt-body-on-dark)] text-lg lg:text-xl max-w-2xl leading-relaxed">
            Answer a few questions. Get a personalized plan and a PDF in minutes.
          </p>
        </div>
      </section>

      {/* plays */}
      <section className="vt-paper">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {plays.map((p) => (
            <div key={p.slug} data-testid={`play-${p.slug}`} className="border-2 border-[color:var(--vt-dark)] bg-[#FBF9F5] p-7 flex flex-col">
              <span className="vt-eyebrow vt-eyebrow--cream">Quick Play {p.number}</span>
              <h2 className="mt-3 font-head text-[color:var(--vt-body-on-cream)] text-5xl leading-[0.86]">
                {p.title_stacked.map((w) => <span key={w} className="block">{w}</span>)}
              </h2>
              <div className="mt-4 flex items-center gap-3">
                <span className="font-[IBM_Plex_Mono] text-xs uppercase tracking-[0.15em] text-[color:var(--vt-label-on-cream)]">{p.audience_label}</span>
                <span className="font-head text-2xl text-[color:var(--vt-body-on-cream)]">{money(p.price_cents)}</span>
              </div>
              <p className="mt-4 text-[color:var(--vt-body-on-cream)] text-sm leading-relaxed">{p.short_description}</p>
              <ul className="mt-4 space-y-1.5 flex-1">
                {p.included.map((i) => (
                  <li key={i} className="text-sm text-[color:var(--vt-body-on-cream)] flex gap-2">
                    <span className="text-[color:var(--vt-amber)]">›</span>{i}
                  </li>
                ))}
              </ul>
              <Link to={`/quick-plays/${p.slug}`} data-testid={`start-${p.slug}`} className="vt-btn vt-btn-secondary-cream mt-6 w-full">START <span className="vt-arrow">→</span></Link>
            </div>
          ))}
        </div>
      </section>

      {/* how it works */}
      <section className="bg-[color:var(--vt-dark)] vt-grain">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <span className="vt-eyebrow">How it works</span>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[["01", "Answer", "A few quick taps about the situation."], ["02", "Pay", "A few dollars, one time. All sales final once delivered."], ["03", "Get your plan", "A personalized result page + PDF, emailed to you."]].map(([n, t, d]) => (
              <div key={n}>
                <span className="font-[IBM_Plex_Mono] text-[color:var(--vt-amber)]">{n}</span>
                <h3 className="mt-1 font-head text-[color:var(--vt-white)] text-2xl">{t}</h3>
                <p className="mt-1 text-[color:var(--vt-graphite)] text-sm">{d}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-[color:var(--vt-graphite)] text-xs max-w-2xl leading-relaxed">
            Quick Plays are coaching and parenting guidance, not medical, mental-health, or legal advice. For injuries, health concerns, or safety issues, contact a qualified professional.
          </p>
        </div>
      </section>
    </div>
  );
}
